import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Input, Form, Typography } from "antd";
import tokens from "../../theme/tokens";
import SubtypeSelect from "./SubtypeSelect";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Admin-side edit modal for a single GoldStandard row.
 *
 * Category is deliberately not editable here because changing it can
 * trip the `(document, category)` uniqueness constraint on the BE
 * (409). For a category swap admins delete the row and re-add via the
 * right-click row action, which is also the trail we want recorded.
 *
 * Subtype IS editable: it carries no uniqueness constraint, and this is
 * the path that matters most in practice. A library of rating-decision
 * gold standards already exists, so labelling those rows in place is
 * far cheaper than deleting and re-adding each one.
 */
const EditGoldStandardModal = ({
  open,
  row,
  subtypes = [],
  saving = false,
  error = "",
  onCancel,
  onSubmit,
}) => {
  const [notes, setNotes] = useState("");
  const [subtype, setSubtype] = useState("");

  useEffect(() => {
    if (!open) return;
    setNotes(row?.notes || "");
    setSubtype(row?.subtype || "");
  }, [open, row]);

  const handleSubmit = () => {
    if (saving) return;
    // Always send the subtype, including "" — clearing a mistaken label
    // has to be possible, and an omitted key would silently keep it.
    onSubmit({ notes: notes.trim(), subtype });
  };

  const filename = row?.filename || row?.document?.name || row?.document_name || "—";
  const category = row?.doc_category || row?._category || row?.category || "—";

  return (
    <Modal
      open={open}
      title="Edit gold standard"
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Save"}
      cancelText="Cancel"
      okButtonProps={{ disabled: saving, "data-testid": "edit-gold-submit" }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="edit-gold-standard-modal"
    >
      <Form layout="vertical">
        <Form.Item label="Document">
          <div
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              background: tokens.surface.page,
              border: `1px solid ${tokens.border.light}`,
              color: tokens.text.primary,
              fontFamily: tokens.typography.fontFamily,
              fontSize: 13,
              wordBreak: "break-word",
            }}
          >
            {filename}
          </div>
        </Form.Item>
        <Form.Item label="Category">
          <div style={{ color: tokens.text.secondary, fontSize: 13 }}>
            {category}
          </div>
        </Form.Item>
        <SubtypeSelect
          subtypes={subtypes}
          value={subtype}
          category={category === "—" ? "" : category}
          disabled={saving}
          onChange={setSubtype}
          testId="edit-gold-subtype"
        />
        <Form.Item
          label="Internal notes"
          tooltip="Visible to admins only."
        >
          <TextArea
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
            placeholder="Optional context for other admins."
            data-testid="edit-gold-notes"
          />
        </Form.Item>
        {error && (
          <Text type="danger" role="alert" data-testid="edit-gold-error">
            {error}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

EditGoldStandardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  subtypes: PropTypes.array,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default EditGoldStandardModal;
